import { useMsal } from "@azure/msal-react";
import React, { useContext, useEffect, useState } from "react";
import { Container, Nav, NavbarBrand } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ReactComponent as MenuHamburguerIcon } from "../assets/icons/MenuHamburg.svg";
import Avatar from "../assets/images/user-marista-default.png";
import Context from "../context/Context";
import DialogMessageProps from "../model/DialogMessageProps";
import { getLabelRoutes, Routes } from "../model/Enums/RoutesEnum";
import { TermoDiretrizEnum } from "../model/Enums/TermoDiretrizEnum";
import { UserRole } from "../model/Enums/UserRoleEnum";
import NavigationGroup from "../model/NavigationGroup";
import NavigationItem from "../model/NavigationItem";
import ModalAcceptPlan from "../msal/ModalAcceptPlan";
import AcceptanceTermsService from "../service/Docente/AcceptanceTermsService";
import {
    StyledButton,
    StyledContainer,
    StyledHrNav,
    StyledNavBar,
    StyledNavBarBrand,
    StyledNavLink,
    StyledSideBar,
    StyledTitle,
    StyledTitleMenu,
    StyledTitleUser,
    StyledVersion,
    StyledTitleContainer,
    StyledNavbarTitle,
} from "../styles/components/NavBarStyles";
import { APP_VERSION, APP_VERSION_DATE } from "../utils/constants";
import GenericUtils from "../utils/GenericUtils";
import DialogMessage from "./DialogMessage";
import {
    ArrowUpIcon,
    CalendarIcon,
    CogIcon,
    FolderIcon,
    WorkflowIcon,
    LogoutIcon,
    ReportIcon,
    ChartSimpleRegular,
    ShieldIcon,
} from "./Icons";
import Menu from "./Menu/Menu";
import NavbarHelp from "./NavbarHelp/NavbarHelp";
import { StyledTarjaAmbiente } from "./ButtonsComponents";

// eslint-disable-next-line sonarjs/cognitive-complexity
const NavBar: React.FC = () => {
    const { instance } = useMsal();
    const { setOpenMenu } = useContext(Context);
    const history = useHistory();
    const account = GenericUtils.getUserAccount();
    const user = GenericUtils.getUser();

    const [open, setOpen] = useState(false);
    const [startOpen, setStartOpen] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);
    const [acceptanceTerm, setAcceptanceTerm] = useState<string>("");
    const [isPRD, setIsPRD] = useState(true);

    const theme = {
        size: open ? "320px" : "84px",
        justifyContent: open ? "space-between" : "center",
        textAlign: open ? "start" : "center",
    };

    const activeStyle = {
        color: "#3d3d3d",
    };

    async function getActivityPlans() {
        if (!user) return;

        const response = await AcceptanceTermsService.getAcceptanceTerms(
            TermoDiretrizEnum.GestaoCargaHoraria,
            user?.docente?.matriculaAdp,
            instance
        );

        if (GenericUtils.isSuccessStatus(response)) {
            setIsOpen(response.data.termoDiretrizId > 0);
            setAcceptanceTerm(
                response.data.htmlTermoDiretriz
                    ? response.data.htmlTermoDiretriz.replace(
                          `<!DOCTYPE html [<!ENTITY nbsp "&#160;"]>`,
                          ""
                      )
                    : ""
            );
        } else {
            const propsDialogError = GenericUtils.getPropsDialogError(response);
            propsDialogError.title =
                propsDialogError.text ===
                "Não foi encontrado período de vigência para a data atual."
                    ? "Vigência não cadastrada"
                    : propsDialogError.title;
            setDialogMessageProps(propsDialogError);
        }
    }

    const handleClick = () => {
        setOpen(!open);
        setOpenMenu(!open);
    };

    const handleLogout = () => {
        GenericUtils.logout(instance);
        history.replace("/");
    };

    useEffect(() => {
        const href = typeof window !== "undefined" ? window.location.href : "";
        if (
            href.includes("agendadocente-qa.pucpr.br") ||
            href.includes("agendadocente-uat.pucpr.br") ||
            href.includes("agendadocente-dev.pucpr.br") ||
            href.includes("localhost")
        ) {
            setIsPRD(false);
        }

        setTimeout(() => {
            if (startOpen) {
                setStartOpen(false);
                setOpen(true);
            }
        }, 1500);
    }, [open]);

    useEffect(() => {
        getActivityPlans();
    }, []);

    const createNavGroupItems = () => {
        return [
            {
                path: "/p",
                title: "Painel",
                icon: ChartSimpleRegular(open ? "#9654ff" : "#4e4e4e", {}),
                required: [UserRole.MeuPainelMenu],

                navItems: [
                    {
                        path: Routes.MeuPainelDocente,
                        title: getLabelRoutes(Routes.MeuPainelDocente),
                        required: [UserRole.MeuPainelDocente],
                    },
                ],
            },
            {
                path: "/n",
                title: "Gestão de Arquivos",
                icon: (
                    <ArrowUpIcon
                        style={{ fill: open ? "#9654ff" : "#4e4e4e" }}
                    />
                ),
                required: [UserRole.Importacao, UserRole.HistoricoImportacao],
                navItems: [
                    {
                        path: Routes.Importar,
                        title: getLabelRoutes(Routes.Importar),
                        required: [UserRole.Importacao],
                    },
                    {
                        path: Routes.Historico,
                        title: getLabelRoutes(Routes.Historico),
                        required: [UserRole.HistoricoImportacao],
                    },
                ],
            },
            {
                path: "/a",
                title: "TACH",
                icon: (
                    <FolderIcon
                        style={{ fill: open ? "#9654ff" : "#4e4e4e" }}
                    />
                ),
                required: [
                    UserRole.PlanoAtividade,
                    UserRole.PlanoAtividadeDocente,
                    UserRole.EditarPlanoAtividadeDocente,
                    UserRole.VisualizarPlanoAtividade,
                    UserRole.ParametrizacaoPlanoAtividade,
                    UserRole.HistoricoTach,
                ],
                navItems: [
                    {
                        path: Routes.ParametrizarPlanoAtividade,
                        title: getLabelRoutes(
                            Routes.ParametrizarPlanoAtividade
                        ),
                        required: [UserRole.ParametrizacaoPlanoAtividade],
                    },
                    {
                        path: Routes.MeuPlano,
                        title: getLabelRoutes(Routes.MeuPlano),
                        required: [UserRole.PlanoAtividade],
                    },
                    {
                        path: Routes.ConsultarPlano,
                        title: getLabelRoutes(Routes.ConsultarPlano),
                        required: [UserRole.VisualizarPlanoAtividade],
                    },
                    {
                        path: Routes.EditarPlano,
                        title: getLabelRoutes(Routes.EditarPlano),
                        required: [UserRole.EditarPlanoAtividadeDocente],
                    },
                    {
                        path: Routes.GestaoTarefa,
                        title: getLabelRoutes(Routes.GestaoTarefa),
                        required: [UserRole.Workflow],
                    },
                    {
                        path: Routes.AdministradorTach,
                        title: getLabelRoutes(Routes.AdministradorTach),
                        required: [UserRole.AdministradorTach],
                    },
                    {
                        path: Routes.HistoricoTach,
                        title: getLabelRoutes(Routes.HistoricoTach),
                        required: [UserRole.HistoricoTach],
                    },
                ],
            },
            {
                path: "/m",
                title: "Agenda Docente",
                icon: (
                    <CalendarIcon
                        style={{ fill: open ? "#9654ff" : "#4e4e4e" }}
                    />
                ),
                required: [
                    UserRole.AgendaDocente,
                    UserRole.EditarAgendaDocente,
                    UserRole.VisualizarAgendaDocente,
                ],
                navItems: [
                    {
                        path: Routes.Calendario,
                        title: getLabelRoutes(Routes.Calendario),
                        required: [UserRole.GestaoCalendario],
                    },
                    {
                        path: Routes.MinhaAgenda,
                        title: getLabelRoutes(Routes.MinhaAgenda),
                        required: [UserRole.AgendaDocente],
                    },
                    {
                        path: Routes.ConsultarAgenda,
                        title: getLabelRoutes(Routes.ConsultarAgenda),
                        required: [UserRole.VisualizarAgendaDocente],
                    },
                    {
                        path: Routes.EditarAgenda,
                        title: getLabelRoutes(Routes.EditarAgenda),
                        required: [UserRole.EditarAgendaDocente],
                    },
                    {
                        path: Routes.ChekinParametro,
                        title: getLabelRoutes(Routes.ChekinParametro),
                        required: [UserRole.ParametrizacaoCheckin],
                    },
                ],
            },
            {
                path: "/w",
                title: "Fluxos de Aprovação",
                icon: (
                    <WorkflowIcon
                        style={{ fill: open ? "#9654ff" : "#4e4e4e" }}
                    />
                ),
                required: [UserRole.GestaoWorkflow],
                navItems: [
                    {
                        path: Routes.Workflows,
                        title: getLabelRoutes(Routes.Workflows),
                        required: [UserRole.GestaoWorkflow],
                    },
                ],
            },
            {
                path: "/r",
                title: "Relatórios",
                icon: (
                    <ReportIcon
                        style={{ fill: open ? "#9654ff" : "#4e4e4e" }}
                    />
                ),
                required: [UserRole.Relatorios],
                navItems: [
                    {
                        path: Routes.Relatorios,
                        title: getLabelRoutes(Routes.Relatorios),
                        required: [UserRole.Relatorios],
                    },
                ],
            },
            {
                path: "/a",
                title: "Configurações",
                icon: (
                    <CogIcon style={{ fill: open ? "#9654ff" : "#4e4e4e" }} />
                ),
                required: [
                    UserRole.AssociacaoAtividadeProfessor,
                    UserRole.AssociacaoProfessorCoordenador,
                    UserRole.GestaoUsuario,
                    UserRole.GestaoPermissaoPlanoAtividade,
                    UserRole.GestaoAbonoLegal,
                ],
                navItems: [
                    {
                        path: Routes.AssociacaoAtividade,
                        title: getLabelRoutes(Routes.AssociacaoAtividade),
                        required: [UserRole.AssociacaoAtividadeProfessor],
                    },
                    {
                        path: Routes.AssociacaoCoordenador,
                        title: getLabelRoutes(Routes.AssociacaoCoordenador),
                        required: [UserRole.AssociacaoProfessorCoordenador],
                    },
                    {
                        path: Routes.GestaoUsuario,
                        title: getLabelRoutes(Routes.GestaoUsuario),
                        required: [UserRole.GestaoUsuario],
                    },
                    {
                        path: Routes.GerenciarPermissao,
                        title: getLabelRoutes(Routes.GerenciarPermissao),
                        required: [UserRole.GestaoPermissaoPlanoAtividade],
                    },
                    {
                        path: Routes.GerenciarAbono,
                        title: getLabelRoutes(Routes.GerenciarAbono),
                        required: [UserRole.GestaoAbonoLegal],
                    },
                    {
                        path: Routes.EditorTermosDeAceite,
                        title: getLabelRoutes(Routes.EditorTermosDeAceite),
                        required: [UserRole.TermoAceite],
                    },
                    {
                        path: Routes.Help,
                        title: getLabelRoutes(Routes.Help),
                        required: [UserRole.Help],
                    },
                ],
            },
        ] as NavigationGroup[];
    };

    const authorizeNavigationMenu = () => {
        const navGroupItems = createNavGroupItems();

        const myUserRoles = user?.listaFuncionalidades?.map((item: any) =>
            Number(item.idFuncionalidade)
        );

        const authorizedGroup = navGroupItems.filter(
            (ng: NavigationGroup) =>
                !!myUserRoles?.some((myRole: UserRole) =>
                    ng.required?.includes(myRole)
                )
        );

        return authorizedGroup.map((ng: NavigationGroup) => {
            const authorizedGrouItems = ng;
            authorizedGrouItems.navItems = ng.navItems.filter(
                (ni: NavigationItem) =>
                    !!myUserRoles?.some((myRole: UserRole) =>
                        ni.required?.includes(myRole)
                    )
            );
            return authorizedGrouItems;
        });
    };

    const menuItem = (title: string) => {
        return (
            open && (
                <StyledTitleMenu style={{ width: "100%" }}>
                    {title}
                </StyledTitleMenu>
            )
        );
    };

    const dialogAceptPlan = isOpen && (
        <ModalAcceptPlan
            open={isOpen}
            onClose={() => {
                setIsOpen(false);
            }}
            term={acceptanceTerm}
        />
    );

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={() => setDialogMessageProps({} as DialogMessageProps)}
        />
    );

    return (
        <ThemeProvider theme={theme}>
            <StyledNavBar
                collapseOnSelect
                style={{
                    position: "fixed",
                    zIndex: "1050",
                    width: "100vw",
                }}
                data-testid="navbar"
            >
                <Container style={{ padding: "0px", margin: "0px" }} fluid>
                    <NavbarBrand>
                        <StyledNavBarBrand>
                            <StyledContainer>
                                {/* Renderização condicional dos ícones com formatação corrigida */}
                                <StyledButton
                                    onClick={handleClick}
                                    title={open ? "Fechar menu" : "Abrir menu"}
                                    aria-label={
                                        open ? "Fechar menu" : "Abrir menu"
                                    }
                                >
                                    <MenuHamburguerIcon />
                                </StyledButton>
                            </StyledContainer>
                        </StyledNavBarBrand>
                        {/* <a
                            href="https://uploadtemplates.blob.core.windows.net/processados/Diretrizes%20Gestao%20da%20Carga%20Horaria.pdf?sp=r&st=2023-05-26T14:10:00Z&se=2023-05-26T22:10:00Z&spr=https&sv=2022-11-02&sr=b&sig=vyPPtL95chbqDXP1cbxHDokKzq8VVu2cmkN37WKVbBo%3D"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Teste
                        </a> */}
                    </NavbarBrand>
                    <StyledNavbarTitle>
                        <StyledTitleContainer>
                            <StyledTitle>
                                Agenda Docente
                                <StyledTarjaAmbiente $show={isPRD}>
                                    Ambiente de testes e homologações
                                </StyledTarjaAmbiente>
                            </StyledTitle>
                            <NavbarHelp />
                        </StyledTitleContainer>
                    </StyledNavbarTitle>
                </Container>
            </StyledNavBar>
            <Nav>
                <StyledSideBar>
                    <Menu
                        authorizedNavGroupItems={authorizeNavigationMenu()}
                        open={open}
                        setOpen={setOpen}
                        forceUpdate={forceUpdate}
                        setForceUpdate={setForceUpdate}
                        activeStyle={activeStyle}
                        menuItem={menuItem}
                    />

                    <StyledNavLink
                        as="a"
                        href="/aviso-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`d-flex w-100 ${
                            open
                                ? "justify-content-start"
                                : "justify-content-center"
                        }`}
                        style={{
                            paddingLeft: 15,
                            paddingRight: 15,
                            marginTop: 20,
                            cursor: "pointer",
                            textDecoration: "none",
                            minHeight: "24px",
                        }}
                    >
                        {!open && <ShieldIcon style={{ fill: "#8a0538" }} />}

                        {open && (
                            <StyledTitleMenu style={{ color: "#8a0538" }}>
                                Aviso de privacidade
                            </StyledTitleMenu>
                        )}
                    </StyledNavLink>

                    <Container
                        style={{
                            flexDirection: "column",
                            paddingTop: 20,
                            alignItems: open ? "flex-start" : "center",
                        }}
                    >
                        <StyledHrNav style={{ marginLeft: open ? 15 : 0 }} />
                        <div
                            className="d-flex align-items-center"
                            style={{
                                marginLeft: open ? 15 : 0,
                            }}
                        >
                            <img
                                src={Avatar}
                                alt="Avatar"
                                style={{
                                    borderRadius: "50%",
                                    width: 40,
                                    height: 40,
                                    margin: open
                                        ? "24px 8px 24px 0"
                                        : "24px 0px",
                                }}
                            />
                            {open && (
                                <StyledTitleUser>
                                    {account?.name}
                                </StyledTitleUser>
                            )}
                        </div>
                        <StyledHrNav style={{ marginLeft: open ? 15 : 0 }} />
                        <StyledNavLink
                            className={`d-flex w-100 mt-4 background-leave ${
                                open
                                    ? "justify-content-start"
                                    : "justify-content-center"
                            }`}
                            onClick={handleLogout}
                            style={{
                                height: 40,
                                paddingLeft: 15,
                                paddingRight: 15,
                                cursor: "pointer",
                            }}
                        >
                            <LogoutIcon style={{ fill: "#4e4e4e" }} />
                            {open && (
                                <StyledTitleMenu style={{ color: "#4e4e4e" }}>
                                    Sair
                                </StyledTitleMenu>
                            )}
                        </StyledNavLink>
                    </Container>
                    <Container>
                        <StyledVersion>
                            {`${APP_VERSION} ${APP_VERSION_DATE}`}
                        </StyledVersion>
                    </Container>
                </StyledSideBar>
            </Nav>
            {dialogAceptPlan}
            {dialogMessage}
        </ThemeProvider>
    );
};

export default NavBar;
